import { getDateFormat } from 'helpers/dates'
import React from 'react'
import { connect } from 'react-redux'
import { injectIntl } from 'react-intl'
import moment from 'moment'
import { FiberManualRecord } from '@material-ui/icons'
import cn from 'classnames'
import {
  fetchJobTaskPositionUpdate,
  tasksFetchTableData,
  deleteTask,
  tasksClearState,
} from 'reducers/jobTasksAdmin/reducer'
import ListFromTable from 'components/common/ListFromTable'
import ColumnCollection from 'types/table/ColumnCollection'
import Column from 'types/table/Column'
import ActionCollection from 'types/table/ActionCollection'
import Action from 'types/table/Action'
import * as ROUTE from 'constants/appRouting'
import UrlHelper from 'helpers/url'
import Sort from 'lib/psfs/Sort'
import { JOBS_WORKFLOW_ADMIN, JOB_WORKFLOWS } from 'constants/appRouting'
import JobsWorkflowAdminPositionChange from '../JobsWorkflowAdminPositionChange'
import messages from './translate'

class TaskList extends ListFromTable {
  constructor(props) {
    super(props)
    this.getTableData = this.getTasksTableData
    this.deleteEntity = props.deleteTask
    this.clearReducerState = props.tasksClearState
    this.asyncData = true
    this.loadingData = true

    this.state = {
      isDrilled: false,
    }
  }

  shouldComponentUpdate(nextProps, nextState, nextContext) {
    const { licenseeId, tableData } = this.props
    const { isDrilled } = this.state
    const {
      licenseeId: nextLicenseeId,
      tableData: nextTableData,
      location: { search },
      loading,
    } = nextProps
    this.loadingData = loading

    if (nextLicenseeId !== licenseeId) {
      this.reloadTableData(ROUTE.JOB_WORKFLOW_TASKS)

      return false
    }

    if (tableData !== nextTableData) {
      this.loadingData = false
    }

    if (search && search.length && !isDrilled) {
      this.setState({
        isDrilled: true,
      })
    }

    return super.shouldComponentUpdate(nextProps, nextState, nextContext)
  }

  getTableProps() {
    const {
      intl: { formatMessage },
      history: { push },
      location: { search },
    } = this.props
    const { isDrilled } = this.state
    const isSearchExist = search && search.length
    let taskTitle = formatMessage(messages.jobWorkflowTaskAdmin)
    let backUrl = JOBS_WORKFLOW_ADMIN

    if (isSearchExist) {
      const queryParams = new URLSearchParams(search)

      taskTitle = queryParams.get('jobTitle')
    }

    if (isSearchExist || isDrilled) {
      backUrl = JOB_WORKFLOWS
    }

    return {
      goBack: () => {
        push(backUrl)
      },
      title: taskTitle,
      showFilterBar: true,
      sorting: true,
      paging: true,
      searching: true,
      creating: true,
    }
  }

  getCreateButton() {
    const {
      intl: { formatMessage },
      location: { search },
    } = this.props
    let params

    if (search && search.length) {
      const incomingParams = new URLSearchParams(search)
      const newParams = new URLSearchParams()

      newParams.append('id', incomingParams.get('jobId'))
      params = newParams.toString()
    }

    return {
      title: formatMessage(messages.createTask),
      link: ROUTE.JOB_WORKFLOW_TASKS_CREATE,
      params,
    }
  }

  getConfirmDialogProps() {
    const {
      intl: { formatMessage },
    } = this.props

    return {
      title: formatMessage(messages.deleteTask),
      message: formatMessage(messages.deleteTaskMessage),
    }
  }

  getColumns() {
    const {
      intl: { formatMessage },
      fetchJobTaskPositionUpdate,
      licensee: { usaDateFormat },
    } = this.props
    const columns = new ColumnCollection()
    const dateDisplay = getDateFormat(usaDateFormat)

    columns.add(new Column('id', 'ID', { sorting: true }))
    columns.add(
      new Column('position', formatMessage(messages.position), {
        sorting: true,
        render: ({ position, id }, rowIndex) => {
          const handlePositionChange = position => {
            fetchJobTaskPositionUpdate({
              params: {
                type: 'job-workflow-tasks',
                id,
              },
              data: {
                position,
              },
              index: rowIndex,
            })
          }

          return (
            <JobsWorkflowAdminPositionChange
              position={position}
              onChange={handlePositionChange}
            />
          )
        },
      }),
    )
    columns.add(
      new Column('title', formatMessage(messages.name), {
        sorting: true,
        defaultSort: true,
        defaultSortOrder: Sort.ASC,
      }),
    )
    columns.add(
      new Column('workflow_title', formatMessage(messages.workflow), {
        sorting: true,
      }),
    )
    columns.add(
      new Column('date_updated', formatMessage(messages.dateUpdated), {
        sorting: true,
        render: row => moment(row.date_updated).format(dateDisplay),
      }),
    )
    columns.add(
      new Column('enabled', formatMessage(messages.active), {
        sorting: true,
        render: ({ enabled }) => (
          <FiberManualRecord
            className={cn('user-status-icon', { 'status-enabled': enabled })}
          />
        ),
      }),
    )

    return columns
  }

  getActions() {
    const {
      history,
      location: { search },
      intl: { formatMessage },
    } = this.props
    const actions = new ActionCollection()

    actions.add(
      new Action('edit', formatMessage(messages.editTask), ({ id }) => {
        history.push(UrlHelper.create(ROUTE.JOB_WORKFLOW_TASKS_EDIT, { id }))
      }),
    )
    actions.add(
      new Action('delete', formatMessage(messages.deleteTask), ({ id }) => {
        this.onOpenConfirmDialog({
          entityId: id,
          psfs: this.psfs,
        })
      }),
    )
    actions.add(
      new Action(
        'arrow_forward',
        formatMessage(messages.goToInputs),
        ({ id, title, workflow_title }) => {
          const params = new URLSearchParams()

          params.append('taskId', id)
          params.append('taskTitle', title)
          params.append('jobTitle', workflow_title)

          if (search && search.length) {
            params.append('backUrlParams', search)
          }

          history.push({
            pathname: ROUTE.JOB_WORKFLOW_INPUT_ITEMS,
            search: params.toString(),
          })
        },
      ),
    )

    return actions
  }

  getTasksTableData() {
    const {
      tasksFetchTableData,
      location: { search },
    } = this.props

    if (search && search.length) {
      const queryParams = new URLSearchParams(search)

      this.psfs.addFilter('workflow', queryParams.get('jobId'))
    }

    tasksFetchTableData({ psfs: this.psfs })
  }
}

export default connect(
  state => ({
    tableData: state.getIn(['jobTasksAdmin', 'tableData']),
    licenseeId: state.getIn(['profile', 'licenseeId']),
    loading: state.getIn(['jobTasksAdmin', 'loading']),
    licensee: state.getIn(['profile', 'profile', 'licensee']),
  }),
  {
    fetchJobTaskPositionUpdate,
    tasksFetchTableData,
    deleteTask,
    tasksClearState,
  },
)(injectIntl(TaskList))
